import { Wind, Thermometer, Gauge, Waves } from "lucide-react";
import { WindCompass } from "@/components/WindCompass";
import { ratingFromKnots } from "@/lib/api";

const colorMap = {
  sky: "bg-sky-100 text-sky-700",
  cyan: "bg-cyan-100 text-cyan-700",
  emerald: "bg-emerald-100 text-emerald-700",
  orange: "bg-orange-100 text-orange-700",
  red: "bg-red-100 text-red-700",
  slate: "bg-slate-100 text-slate-600",
};

export function LiveDashboard({ current }) {
  if (!current) return null;
  const kn = current.wind_speed;
  const rating = ratingFromKnots(kn);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4" data-testid="live-dashboard">
      {/* Wind speed big */}
      <div className="sm:col-span-2 sm:row-span-2 bg-white rounded-3xl border border-slate-100 shadow-sm p-6 flex flex-col justify-between relative overflow-hidden">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">
            Vento attuale
          </span>
          <span className={`text-xs font-bold px-3 py-1 rounded-full ${colorMap[rating.color]}`}>
            {rating.label}
          </span>
        </div>
        <div className="flex items-end gap-2 mt-4">
          <span
            className="font-head text-7xl sm:text-8xl font-black tracking-tighter text-slate-900 leading-none"
            data-testid="wind-speed-value"
          >
            {kn != null ? Math.round(kn) : "--"}
          </span>
          <span className="font-head text-2xl font-bold text-sky-600 mb-2">nodi</span>
        </div>
        <div className="flex items-center gap-2 mt-4 text-slate-500">
          <Wind className="h-4 w-4" />
          <span className="text-sm font-medium">
            Direzione {current.wind_compass || "N/D"} · {current.wind_direction ?? "--"}°
          </span>
        </div>
        <Waves className="absolute -bottom-6 -right-4 h-40 w-40 text-sky-50" />
      </div>

      {/* Compass */}
      <div className="sm:col-span-2 sm:row-span-2 bg-white rounded-3xl border border-slate-100 shadow-sm p-6 flex items-center justify-center">
        <WindCompass
          direction={current.wind_direction}
          compass={current.wind_compass}
          knots={kn}
        />
      </div>

      {/* Gusts */}
      <div className="sm:col-span-2 bg-white rounded-3xl border border-slate-100 shadow-sm p-5 flex items-center gap-4">
        <div className="h-12 w-12 rounded-2xl bg-orange-100 flex items-center justify-center">
          <Gauge className="h-6 w-6 text-orange-600" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-400">Raffiche</div>
          <div className="font-head text-3xl font-extrabold text-slate-900">
            {current.wind_gusts != null ? Math.round(current.wind_gusts) : "--"}
            <span className="text-base text-slate-400 font-bold ml-1">kn</span>
          </div>
        </div>
      </div>

      {/* Temp */}
      <div className="sm:col-span-2 bg-white rounded-3xl border border-slate-100 shadow-sm p-5 flex items-center gap-4">
        <div className="h-12 w-12 rounded-2xl bg-sky-100 flex items-center justify-center">
          <Thermometer className="h-6 w-6 text-sky-600" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-400">Temperatura</div>
          <div className="font-head text-3xl font-extrabold text-slate-900">
            {current.temperature != null ? Math.round(current.temperature) : "--"}
            <span className="text-base text-slate-400 font-bold ml-1">°C</span>
          </div>
        </div>
      </div>
    </div>
  );
}
